import * as fs from "node:fs";
import { CHANNEL_LATEST_URL, channelUrlForVersion, platformTag } from "@m8t-stack/platform-release";
import { validateManifest, type ReleaseManifest } from "./release-manifest.js";
import { getGhToken } from "./gh-helpers.js";
import { LocalCliError } from "./errors.js";

export { CHANNEL_LATEST_URL, channelUrlForVersion };

export type ManifestSource =
  | { file: string }
  | { url: string }
  | { channel: true; version?: string };

/**
 * The `ManifestSource` for a specific version TAG (e.g. "platform-v0.1.0"),
 * honoring a custom bicep-set channel host — the ONE rule both the updater's
 * forward fetch (converge.ts) and its rollback fetch (platform-health-gate.ts)
 * use so neither ever needs GitHub repo auth against a custom test/mirror host:
 *   - default channel (empty or the canonical GitHub "latest" URL): the
 *     canonical per-version release asset via `{ channel: true, version: tag }`.
 *   - a GitHub-style "latest" URL: swap the `latest/download` segment for
 *     `download/<tag>` so the per-version pin is honored on the operator's host.
 *   - any other custom shape: fetch it directly (the caller asserts the version).
 */
export function manifestSourceForVersionTag(channelUrl: string, version: string): ManifestSource {
  // Normalize to the canonical tag FIRST (via the one helper) so a bare version
  // (e.g. the rollback path's previousPlatformVersion) never produces a
  // `…/download/0.2.0/…` URL that 404s against the tag-keyed release asset.
  const tag = platformTag(version);
  const custom = channelUrl.trim();
  if (!custom || custom === CHANNEL_LATEST_URL) return { channel: true, version: tag };
  if (custom.includes("/releases/latest/download/")) {
    return { url: custom.replace("/releases/latest/download/", `/releases/download/${tag}/`) };
  }
  return { url: custom };
}

interface Deps {
  fetchImpl?: typeof fetch;
  ghToken?: () => Promise<string>;
  readFile?: (p: string) => string;
}

export async function fetchManifest(source: ManifestSource, deps: Deps = {}): Promise<ReleaseManifest> {
  const readFile = deps.readFile ?? ((p: string) => fs.readFileSync(p, "utf8"));
  const fetchImpl = deps.fetchImpl ?? fetch;
  const ghToken = deps.ghToken ?? getGhToken;

  let raw: string;
  if ("file" in source) {
    try {
      raw = readFile(source.file);
    } catch (e) {
      throw new LocalCliError({ code: "PLATFORM_MANIFEST_READ_FAILED", message: `Could not read manifest file '${source.file}': ${(e as Error).message}` });
    }
  } else {
    const url = "url" in source ? source.url : source.version ? channelUrlForVersion(source.version) : CHANNEL_LATEST_URL;
    // gh token is best-effort — the day the repo goes public it becomes optional.
    let token: string | undefined;
    try { token = await ghToken(); } catch { token = undefined; }
    const res = await fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      throw new LocalCliError({
        code: "PLATFORM_MANIFEST_FETCH_FAILED",
        message: `GET ${url} returned HTTP ${res.status.toString()}.`,
        hint: "The release channel is auth-gated until the repo is public. Run 'gh auth login', or pass --manifest-file / --manifest-url.",
      });
    }
    raw = JSON.stringify(await res.json());
  }

  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch (e) {
    throw new LocalCliError({ code: "PLATFORM_MANIFEST_PARSE_FAILED", message: `Manifest is not valid JSON: ${(e as Error).message}` });
  }
  const errors = validateManifest(parsed);
  if (errors.length > 0) {
    throw new LocalCliError({
      code: "PLATFORM_MANIFEST_INVALID",
      message: `Fetched manifest failed validation:\n  - ${errors.join("\n  - ")}`,
    });
  }
  return parsed as ReleaseManifest;
}
